import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Typography,
  Checkbox,
  Collapse,
  Row,
  Col,
  Radio,
  Tag,
  Space,
} from "antd";
import {
  PlusOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
} from "@ant-design/icons";
import { API_BASE_URL } from "../api/api";
import CustomNumberInput from "../components/CustomNumberInput";

const { Title } = Typography;

const AgencyPage = () => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");

  const tourOperators = [
    { code: "CAH", name: "Caribe Sol", rate: "8" },
    { code: "ETG", name: "Enjoy Travel Group", rate: "8" },
    { code: "HOL", name: "Holasun", rate: "8" },
    { code: "SQV", name: "Sunquest Vacations", rate: "9" },
    { code: "STC", name: "The Special Travel", rate: "10" },
    { code: "SWG", name: "Sunwing Vacations", rate: "8" },
    { code: "TBA", name: "TravelBrands", rate: "9" },
    { code: "VAC", name: "Air Canada Vacations", rate: "9" },
    { code: "VAT", name: "Transat", rate: "9" },
    { code: "VWQ", name: "Vacances Westjet Quebec", rate: "8" },
    { code: "WJS", name: "WestJet Vacations", rate: "8" },
  ];

  const [agencyCode, setAgencyCode] = useState("");
  const [agencies, setAgencies] = useState([]);

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}agents`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const json = await res.json();

        if (res.ok && json.success) {
          // Keep all API fields so table can render them
          const formatted = json.data.map((a) => ({
            key: a.id,
            ...a, // include all fields from API response
          }));
          setAgencies(formatted);
        } else {
          message.error(json.error || "Failed to fetch agencies");
        }
      } catch (err) {
        console.error(err);
        message.error("Something went wrong while fetching agencies");
      }
    };

    fetchAgencies();
  }, [token]);

  // Function to resend invitation email
  const resendInvitation = async (email) => {
    if (!email) {
      message.error("No email available to resend invitation.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}resend-invitation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email }),
      });

      const json = await res.json();

      if (res.ok) {
        message.success("Invitation email resent successfully.");
      } else {
        message.error(json.error || "Failed to resend invitation.");
      }
    } catch (err) {
      console.error(err);
      message.error("Error occurred while resending invitation.");
    }
  };

  const showModal = () => {
    form.resetFields();
    setOpen(true);
  };

  const handleCancel = () => {
    form.resetFields();
    setAgencyCode("");
    setOpen(false);
  };

  const onFinish = async (values) => {
    setLoading(true);

    try {
      const formattedMarkup = {};

      tourOperators.forEach(({ code, name }) => {
        const valObj = values.markup?.[name] || {};
        const rawValue = typeof valObj.value === "number" ? valObj.value : 0;
        const type = valObj.type === "fixed" ? "fixed" : "percentage";
        const value = type === "percentage" ? rawValue / 100 : rawValue;

        formattedMarkup[code] = { value, type };
      });

      const payload = {
        ...values,
        markup: formattedMarkup,
        role_id: 3,
      };

      // console.log("payload", payload);

      const res = await fetch(`${API_BASE_URL}register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (res.ok && json.message) {
        message.success("Agency registered successfully");
        setOpen(false);

        setAgencies((prev) => [
          ...prev,
          {
            key: `${Date.now()}`,
            ...values,
          },
        ]);
      } else {
        message.error(json.error || "Failed to register agency");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const markupTypes = Form.useWatch(["markup"], form) || {};

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Agency Name",
      dataIndex: "agency_name",
      key: "agency_name",
    },
    {
      title: "Agency Code",
      dataIndex: "agency_code",
      key: "agency_code",
    },
    ...[
      "CAH",
      "ETG",
      "HOL",
      "SQV",
      "STC",
      "SWG",
      "TBA",
      "VAC",
      "VAT",
      "VWQ",
      "WJS",
    ].flatMap((key) => [
      {
        title: key,
        dataIndex: key,
        key: key,
        render: (val, record) => {
          // val is string like "0.0000"
          // record has the corresponding type field, e.g. "CAH_type"
          const type = record[`${key}_type`];
          if (!val || val === "0.0000") return "0";

          const numVal = parseFloat(val);
          if (isNaN(numVal)) return "-";

          if (type === "fixed") {
            // fixed = dollars
            return `$${numVal.toFixed(2)}`;
          } else if (type === "percentage") {
            // percentage, show with %
            return `${(numVal * 100).toFixed(2)}%`;
          } else {
            // fallback if type is missing
            return numVal.toFixed(2);
          }
        },
      },
    ]),
    {
      title: "Markup Allowed",
      dataIndex: "agency_markup_allowed",
      key: "agency_markup_allowed",
      render: (val) =>
        val === 1 ? (
          <CheckCircleTwoTone twoToneColor="#52c41a" />
        ) : (
          <CloseCircleTwoTone twoToneColor="#ff4d4f" />
        ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
    },
    {
      title: "Account Status",
      dataIndex: "account_status",
      key: "account_status",
      render: (status, record) => {
        let color = "default";
        let text = "";

        switch (status) {
          case "waiting_for_activation":
            color = "orange";
            text = "Waiting for Activation";
            break;
          case "activated":
            color = "green";
            text = "Activated";
            break;
          case "no_user_found":
            color = "red";
            text = "Test Account (Dev)";
            break;
          default:
            text = "-";
        }

        return (
          <Space>
            <Tag color={color}>{text}</Tag>
            {status === "waiting_for_activation" && (
              <Button
                size="small"
                type="primary"
                onClick={() => resendInvitation(record.email)}
              >
                Resend Invitation
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Agencies</Title>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
        onClick={showModal}
      >
        Create Agency
      </Button>

      <Table
        columns={columns}
        dataSource={agencies}
        bordered
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Create New Agency"
        open={open}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        width={1200}
      >
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ required: true, message: "Please input the email" }]}
              >
                <Input type="email" placeholder="Email" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Agency Code"
                name="agency_code"
                rules={[
                  { required: true, message: "Please input agency code" },
                  {
                    pattern: /^[a-zA-Z]+$/,
                    message: "Only alphabetic characters are allowed",
                  },
                ]}
              >
                <Input
                  placeholder="Unique agency code"
                  onChange={(e) => setAgencyCode(e.target.value)}
                />
              </Form.Item>
              <div style={{ marginTop: -12, marginBottom: 8, color: "#888" }}>
                Domain will be: <strong>{agencyCode}.sunbook.ca</strong>
              </div>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="First Name"
                name="first_name"
                rules={[{ required: true, message: "Please input first name" }]}
              >
                <Input placeholder="First name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Last Name"
                name="last_name"
                rules={[{ required: true, message: "Please input last name" }]}
              >
                <Input placeholder="Last name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="allow_custom_markup"
                valuePropName="checked"
                initialValue={false}
              >
                <Checkbox>
                  Allow this agency to define their own markup
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Collapse defaultActiveKey={["1"]} style={{ marginBottom: 16 }}>
            <Collapse.Panel
              header="Default Markup per Tour Operator (%)"
              key="1"
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                  gap: 16,
                }}
              >
                {tourOperators.map(({ code, name, rate }) => {
                  const type = markupTypes?.[name]?.type || "percentage";

                  return (
                    <Form.Item
                      key={code}
                      label={
                        <div>
                          <span>
                            <strong>{code}</strong> {name}
                          </span>
                          <br />
                          <span>Commission: {rate}%</span>
                        </div>
                      }
                    >
                      <Row gutter={8}>
                        <Col span={16}>
                          <Form.Item name={["markup", name, "value"]} noStyle>
                            <CustomNumberInput
                              isFixed={type === "fixed"}
                              isPercentage={type === "percentage"}
                              step={0.5}
                              min={0}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            name={["markup", name, "type"]}
                            initialValue="percentage"
                            noStyle
                          >
                            <Radio.Group>
                              <Radio value="percentage">%</Radio>
                              <Radio value="fixed">$</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form.Item>
                  );
                })}
              </div>
            </Collapse.Panel>
          </Collapse>

          <Form.Item style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AgencyPage;
